import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Plane,
  Settings
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useDialog } from '../contexts/DialogContext';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const Aircraft = () => {
  const { confirm } = useDialog();
  const [aircraft, setAircraft] = useState([]);
  const [filteredAircraft, setFilteredAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAircraft = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/aircraft', getAuthHeaders());
      setAircraft(response.data);
    } catch (error) {
      console.error('Error fetching aircraft:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to view aircraft');
        // Optionally redirect to login
      } else {
        toast.error('Failed to load aircraft');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAircraft();
  }, [fetchAircraft]);

  const deleteAircraft = async (id, aircraftId) => {
    if (await confirm({ title: `Delete aircraft ${aircraftId}?`, message: 'This removes the aircraft from your aircraft list.', confirmLabel: 'Delete', tone: 'danger' })) {
      try {
        await axios.delete(`/api/aircraft/${id}`, getAuthHeaders());
        toast.success('Aircraft deleted successfully');
        fetchAircraft();
      } catch (error) {
        console.error('Error deleting aircraft:', error);
        if (error.response?.status === 401) {
          toast.error('Please log in to delete aircraft');
        } else if (error.response?.data?.error?.includes('associated flight records')) {
          toast.error('Cannot delete aircraft with existing flight records');
        } else {
          toast.error('Failed to delete aircraft');
        }
      }
    }
  };

  useEffect(() => {
    if (!searchTerm) {
      setFilteredAircraft(aircraft);
      return;
    }

    const filtered = aircraft.filter(ac =>
      ac.aircraft_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ac.aircraft_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ac.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ac.model?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredAircraft(filtered);
  }, [aircraft, searchTerm]);

  const getEngineTypeDisplay = (engineType) => {
    if (!engineType) return 'Unknown';

    const types = {
      piston: 'Piston',
      reciprocating: 'Reciprocating',
      turboprop: 'Turboprop',
      'turbo-prop': 'Turboprop',
      turbojet: 'Turbojet',
      jet: 'Jet',
      'turbo-fan': 'Turbofan',
      turbofan: 'Turbofan',
      electric: 'Electric'
    };

    return types[engineType.toLowerCase()] || 'Unknown';
  };

  const getComplexityBadges = (aircraft) => {
    const badges = [];
    if (aircraft.complex) badges.push('Complex');
    if (aircraft.high_performance) badges.push('High Performance');
    if (aircraft.retractable_gear) badges.push('Retractable');
    if (aircraft.constant_speed_prop) badges.push('Constant Speed');
    if (aircraft.glass_cockpit) badges.push('Glass Cockpit');
    return badges;
  };

  // Your JSX remains the same...
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Rest of your existing JSX */}
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
              <Settings className="h-8 w-8 mr-3 text-blue-600" />
              Aircraft Management
            </h1>
            <p className="text-gray-600">
              Manage your aircraft fleet and specifications
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link 
              to="/add-aircraft" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Add Aircraft</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="relative">
          <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search aircraft by tail number, type, or manufacturer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Aircraft Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAircraft.map((ac) => (
          <div key={ac.id} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-600 p-2 rounded-lg">
                  <Plane className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {ac.aircraft_id}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {ac.aircraft_type}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <Link
                  to={`/edit-aircraft/${ac.id}`}
                  className="text-blue-600 hover:text-blue-700 p-1"
                  title="Edit Aircraft"
                >
                  <Edit className="h-4 w-4" />
                </Link>
                <button
                  onClick={() => deleteAircraft(ac.id, ac.aircraft_id)}
                  className="text-red-600 hover:text-red-700 p-1"
                  title="Delete Aircraft"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {/* Manufacturer & Model */}
              {(ac.manufacturer || ac.model) && (
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Make/Model:</span> {ac.manufacturer} {ac.model}
                  </p>
                </div>
              )}

              {/* Year */}
              {ac.year && (
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Year:</span> {ac.year}
                  </p>
                </div>
              )}

              {/* Engine Type */}
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Engine:</span> {getEngineTypeDisplay(ac.engine_type)}
                </p>
              </div>

              {/* Registration Country */}
              {ac.registration_country && (
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Registration:</span> {ac.registration_country}
                  </p>
                </div>
              )}

              {/* Complexity Badges */}
              {getComplexityBadges(ac).length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Characteristics:</p>
                  <div className="flex flex-wrap gap-1">
                    {getComplexityBadges(ac).map((badge, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {ac.notes && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Notes:</p>
                  <p className="text-sm text-gray-700">
                    {ac.notes.length > 100 ? `${ac.notes.substring(0, 100)}...` : ac.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAircraft.length === 0 && (
        <div className="text-center py-12">
          <Plane className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            {searchTerm ? 'No aircraft match your search' : 'No aircraft in your fleet yet'}
          </p>
          <Link
            to="/add-aircraft"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>Add Your First Aircraft</span>
          </Link>
        </div>
      )}

      {/* Aircraft Count */}
      {filteredAircraft.length > 0 && (
        <div className="mt-6 text-sm text-gray-600 text-center">
          Showing {filteredAircraft.length} of {aircraft.length} aircraft
        </div>
      )}
    </div>
  );
};

export default Aircraft;